/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";

import { Box, Card, Divider, Tab, Tabs, useMediaQuery } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  CreateButton,
  Datagrid,
  EditButton,
  FilterButton,
  FunctionField,
  ListContextProvider,
  ListToolbar,
  RefreshButton,
  SimpleList,
  TextField,
  Title,
  TopToolbar,
  useListContext,
} from "react-admin";

import { CarrierUserTypeChoices, UserType } from "../../../../utils/constants";

import { isSubscriptionDisabled } from "../../../../utils/appUtils";

import { ChevronRightIcon } from "../../../../components/common/iconography";
import { ForceTitle } from "../../../../components/common/ForceTitle";
import { SearchField } from "../../../../components/common/SearchField";
import { Pagination } from "../../../../components/Pagination";
import { Empty } from "../../../../components/common/Empty";
import { CarrierDropDown } from "./CarrierDropDown";
import { DriverCreateButton } from "./DriverCreateDialog";

import { useSearchableList } from "../../../../dataProvider/useSearchableList";
import { UserStatusIcon } from "../../users/UserStatusIcon";

const useStyles = makeStyles((theme) => ({
  statusIcon: {
    marginTop: theme.spacing(1),
  },
}));

const renderName = (record) => {
  return `${record.lastName}, ${record.firstName}`;
};

const CarrierSearchField = ({ label, alwaysOn, ...props }) => {
  const listContext = useListContext(props);
  const { displayedFilters, filterValues, setFilters } = listContext;

  const handleSearch = (value) => {
    debugger;
    if (value) {
      const nextFilterValues = { ...filterValues };
      delete nextFilterValues.or;

      const filters = {
        ...nextFilterValues,
        or: [
          { businessName: { matchPhrasePrefix: `${value}` } },
          { businessName: { wildcard: `*${value}*` } },
          { lastName: { matchPhrasePrefix: `${value}` } },
          { lastName: { wildcard: `*${value}*` } },
          { email: { matchPhrasePrefix: `${value}` } },
          { email: { wildcard: `*${value}*` } },
        ],
      };

      setFilters(filters, displayedFilters);
    } else {
      const nextFilterValues = { ...filterValues };
      delete nextFilterValues.or;
      setFilters(nextFilterValues, displayedFilters);
    }
  };

  return (
    <SearchField
      id="searchTerm"
      label={label}
      margin="dense"
      variant="outlined"
      alwaysOn={alwaysOn}
      handleSearch={handleSearch}
    />
  );
};

const getListFilters = (mdDown) => [
  <CarrierDropDown source="carrierId.eq" label="Carrier" alwaysOn={!mdDown} />,
  <CarrierSearchField label="Search" alwaysOn />,
];

const tabs = [
  { id: "dispatch", name: "Dispatch" },
  { id: "drivers", name: "Drivers" },
];

// Key-mode tab prefix for the reloadYardCarrierByActiveByTypeByName GSI. One
// tab is one type, so the begins_with prefix selects a tab and the rest of the
// composite sort key (businessName#lastName) orders it. Legacy null, short-form
// and CARRIER_DISPATCH_ADMIN types are canonicalized by the backfill migration
// and by sanitizeUserReloadCarrier on every later write, so every row carries
// one of the two canonical values here. A row the backfill has not reached yet
// still lands in the right tab: "CARRIER_DISPATCH_ADMIN" begins with
// "CARRIER_DISPATCH", so it sorts after the canonical rows rather than
// vanishing.
const tabKeyTypePrefix = [UserType.CARRIER_DISPATCH, UserType.CARRIER_DRIVER];

// Search-mode (OpenSearch) equivalent, applied while the user is actively
// filtering. Dispatch uses `ne` (not `eq: CARRIER_DISPATCH`) so that a
// not-yet-backfilled legacy row still matches.
const tabTypeFilter = [
  { ne: UserType.CARRIER_DRIVER },
  { eq: UserType.CARRIER_DRIVER },
];

const getSessionTab = () => {
  const sessionTab = Number(sessionStorage.getItem("CarrierUsersTab"));
  if (sessionTab && tabs[sessionTab]) {
    return sessionTab;
  }
  return 0;
};

// Create means two different things by tab. On Dispatch it invites a user by
// email (the create page). On Drivers it creates the driver outright, for the
// exception case of a driver who cannot sign up in the Driver App themselves
// — a dialog, since there is no invite to wait on.
const ListActions = ({ basePath, xsDown, isDrivers, ...props }) => {
  return !xsDown ? (
    <TopToolbar>
      <FilterButton />
      {isDrivers ? (
        <DriverCreateButton disabled={isSubscriptionDisabled()} />
      ) : (
        <CreateButton basePath={basePath} disabled={isSubscriptionDisabled()} />
      )}
      <RefreshButton />
    </TopToolbar>
  ) : null;
};

export const CarrierUserList = (props) => {
  const classes = useStyles();
  const xsDown = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const mdDown = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const [tabIndex, setTabIndex] = useState(getSessionTab());

  // The tab is part of the key-mode query itself: the GSI's composite sort
  // key is active#type#businessName#lastName, so each tab is one begins_with
  // prefix. Reading DynamoDB directly (not OpenSearch) is what keeps the list
  // honest right after a delete — the search index lags the write, the GSI
  // doesn't. New identity per tab so useSearchableList refetches.
  const defaultFilter = useMemo(
    () => ({
      reloadYardId: window.appUser?.reloadYardId,
      activeTypeBusinessNameLastName: {
        beginsWith: { active: "true", type: tabKeyTypePrefix[tabIndex] },
      },
    }),
    [tabIndex],
  );

  // Search mode (any user filter set) still goes through OpenSearch, so the
  // tab has to travel as a search filter there.
  const defaultSearchFilter = {
    reloadYardId: { eq: window.appUser?.reloadYardId },
    active: { eq: "true" },
    type: tabTypeFilter[tabIndex],
  };

  // Include key & search to display default sort order.
  const defaultSort = {
    field: `{"key":"reloadYardCarrierByActiveByTypeByName", "search":"businessName"}`,
    order: "ASC",
  };

  // memorize perPage settings
  // see useEffect [listContext?.perPage]) below
  const sessionPerPage = localStorage.getItem(`${props.resource}_perPage`);
  const [defaultPerPage, setDefaultPerPage] = useState(
    sessionPerPage ? Number(sessionPerPage) : 10,
  );

  const listContext = useSearchableList({
    ...props,
    defaultTitle: "Carrier Users",
    defaultFilter: defaultFilter,
    defaultSort: defaultSort,
    defaultSearchFilter: defaultSearchFilter,
    defaultPerPage: defaultPerPage,
    resource: "carrierReloadYards",
  });

  useEffect(() => {
    if (listContext?.perPage) {
      localStorage.setItem(`${props.resource}_perPage`, listContext.perPage);
      setDefaultPerPage(Number(listContext.perPage));
    }
  }, [listContext?.perPage]);

  useEffect(() => {
    if (listContext?.filter) {
      // reset list on automatic stale list reset
      // Extract the key query from defaultSort
      const fieldObj = JSON.parse(defaultSort.field);
      const keyToCheck = fieldObj.key;

      // Check for the key's presence in filter
      const hasKey = keyToCheck in listContext.filter;
      const hasFilterValues = Object.keys(listContext.filterValues).length > 0;
      if (hasKey && hasFilterValues) {
        listContext.setFilters({});
      }
    }
  }, [listContext?.filter]);

  const handleTabChange = (event, value) => {
    sessionStorage.setItem("CarrierUsersTab", value);
    setTabIndex(value);
  };

  return (
    <ListContextProvider value={listContext}>
      <Title title={<ForceTitle title="Carrier Users" />} />
      <ListToolbar
        {...props}
        filters={getListFilters(mdDown)}
        actions={
          <ListActions
            xsDown={xsDown}
            isDrivers={tabs[tabIndex].id === "drivers"}
          />
        }
      />
      <Card>
        <Tabs
          value={tabIndex}
          indicatorColor="primary"
          textColor="primary"
          onChange={handleTabChange}
        >
          {tabs.map((itm, idx) => (
            <Tab value={idx} key={itm.id} label={itm.name} />
          ))}
        </Tabs>
        <Divider />
        <CarrierGrid
          xsDown={xsDown}
          mdDown={mdDown}
          classes={classes}
          tab={tabs[tabIndex].id}
          basePath="/carrierUsers"
          resource="carrierUsers"
        />
        <Pagination {...listContext} />
      </Card>
    </ListContextProvider>
  );
};

const CarrierGrid = (props) => {
  const { classes, xsDown, mdDown, tab } = props;
  const isDrivers = tab === "drivers";
  const { data, ids, page, ...context } = useListContext();

  // Transform the data here
  const transformedData = {};
  const transformedIds = [];

  if (data && ids) {
    ids.forEach((id) => {
      const record = data[id];
      if (record && record.carrierUserId) {
        // Key by carrierUserId so the Datagrid/SimpleList iteration id
        // matches record.id — rowClick "edit" navigates to
        // /carrierUsers/{carrierUserId}, which is what getCarrierUser expects.
        transformedData[record.carrierUserId] = {
          ...record.user,
          id: record.carrierUserId,
          businessName: record.businessName || record.user?.businessName,
          firstName: record.firstName || record.user?.firstName,
          lastName: record.lastName || record.user?.lastName,
          email: record.email || record.user?.email,
          phone: record.phone || record.user?.phone,
          type: record.type,
          // The list is scoped to this transload, so the junction's block is the
          // one the status icon should report.
          blockAccess: record.blockAccess,
        };
        transformedIds.push(record.carrierUserId);
      }
    });
  }

  const newContext = {
    ...context,
    data: transformedData,
    ids: transformedIds,
    basePath: props.basePath,
    resource: props.resource,
  };

  if (ids.length === 0 && page === 1)
    return isDrivers ? (
      <Box textAlign="center" m={1}>
        <Empty
          {...props}
          // Empty's own Create goes to the dispatch invite page.
          hasCreate={false}
          message="No drivers enrolled yet — drivers create their profile in the Driver App"
        />
        <DriverCreateButton disabled={isSubscriptionDisabled()} />
      </Box>
    ) : (
      <Empty {...props} message="Invite a new carrier" />
    );
  return (
    <ListContextProvider value={newContext}>
      {xsDown ? (
        <SimpleList
          primaryText={(record) => record.businessName}
          secondaryText={(record) =>
            isDrivers ? record.driverCode || record.phone : record.email
          }
          tertiaryText={renderName}
          linkType="edit"
          leftIcon={(record) => <UserStatusIcon record={record} />}
          rightIcon={() => (
            <ChevronRightIcon
              fontSize="large"
              color="secondary"
              style={{ marginLeft: 40, marginTop: 10 }}
            />
          )}
        />
      ) : mdDown ? (
        <Datagrid rowClick="edit">
          <TextField
            source="businessName"
            label="Carrier"
            sortable={true}
            sortBy={`{"key":"reloadYardCarrierByActiveByTypeByName", "search":"businessName"}`}
          />
          <FunctionField
            source="lastName"
            label="Name"
            render={renderName}
            sortable={false}
          />
          <UserStatusIcon
            source="status"
            label="Status"
            classes={classes.statusIcon}
            sortable={false}
          />
          {isDrivers ? (
            <TextField source="driverCode" label="Driver code" sortable={false} />
          ) : (
            <FunctionField
              source="type"
              label="Role"
              render={(record) =>
                CarrierUserTypeChoices.find((c) => c.id === record.type)
                  ?.name ??
                record.type ??
                ""
              }
              sortable={false}
            />
          )}
          {isDrivers ? (
            <TextField source="phone" label="Phone" sortable={false} />
          ) : (
            <TextField source="email" label="Email" sortable={false} />
          )}
        </Datagrid>
      ) : (
        <Datagrid rowClick="edit">
          <TextField
            source="businessName"
            label="Carrier"
            sortable={true}
            sortBy={`{"key":"reloadYardCarrierByActiveByTypeByName", "search":"businessName"}`}
          />
          <FunctionField
            source="lastName"
            label="Name"
            render={renderName}
            sortable={false}
          />
          <UserStatusIcon
            source="status"
            label="Status"
            classes={classes.statusIcon}
            sortable={false}
          />
          {isDrivers ? (
            <TextField source="driverCode" label="Driver code" sortable={false} />
          ) : (
            <FunctionField
              source="type"
              label="Role"
              render={(record) =>
                CarrierUserTypeChoices.find((c) => c.id === record.type)
                  ?.name ??
                record.type ??
                ""
              }
              sortable={false}
            />
          )}
          {!isDrivers && (
            <TextField source="email" label="Email" sortable={false} />
          )}
          <TextField source="phone" label="Phone" sortable={false} />
          <EditButton />
        </Datagrid>
      )}
    </ListContextProvider>
  );
};
